<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t, locale } = useI18n()

import { ref, onMounted, reactive, computed } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import { DatePicker as KDatePicker } from '@progress/kendo-vue-dateinputs'
import { IntlProvider, LocalizationProvider } from '@progress/kendo-vue-intl'
import { getKendoLanguage, getKendoLocale } from '@/utils/kendoHelper'
import { GetCountriesFromLocalStorageOrApi } from '../services/CountryCultureService'
import { getCountryLabelKey } from '@/config/countries'
import { CreateAccount } from '../services/CustomerService'
import type { IAoeRegistration, IDefaultWebAccountResponse } from '../types/CustomerTypes.js'
import { GetCountryCode, GetPasswordRegex, GetLoginUrl } from '../stores/SettingsStore.ts'
import { getPasswordProhibitedCharMessage } from '../validators/StringValidator'
import { trackRegister } from '../analytics/index'
import { SetIsLoading } from '../services/AoeHelpers.ts'
import PhoneInputControl from './PhoneInputControl.vue'
import type { IPhoneOutput } from '../types/CommonTypes'
import { ShareCddCodeGetList } from '../api/AccountInfo'
import { BeeEntityWebAccountDefaultGet } from '@/api/Business'
import type { IShareCddCodeGetListResponseItem } from '../types/AccountInfoTypes'
import { handleError } from '@/services/ErrorService'
import { UrlHelpers } from '@/utils/urlHelpers'

const logger = useLogger()


// Map country code to culture name
function getCultureNameFromCountryCode(countryCode: string): string {
  const cultureMap: Record<string, string> = {
    'US': 'en-US',
    'DE': 'de-DE',
    'AT': 'de-AT',
    'FR': 'fr-FR'
  }
  return cultureMap[countryCode] || 'en-US' // Default to en-US if not found
}

interface Country {
  text: string
  id: string
}

const countries = ref<Country[]>([])
const selectedCountry = ref<Country>({ text: '', id: '' })

const formInitialValues = reactive<IAoeRegistration>({ } as IAoeRegistration)

// Phone models for PhoneInputControl (IPhoneOutput format)
const primaryPhoneModel = ref<IPhoneOutput>({
  number: '',
  countryCode: '',
  codeIdPhoneType: 'HOME',
  extension: null,
  formattedNumber: ''
})
const mobilePhoneModel = ref<IPhoneOutput>({
  number: '',
  countryCode: '',
  codeIdPhoneType: 'CELL',
  extension: null,
  formattedNumber: ''
})

// Phone input refs for validation
const primaryPhoneControl = ref<InstanceType<typeof PhoneInputControl> | null>(null)
const mobilePhoneControl = ref<InstanceType<typeof PhoneInputControl> | null>(null)

// Country options for phone input (limited list)
const phoneCountryOptions = [
  { text: 'DE', id: 'DE' },
  { text: 'AT', id: 'AT' },
  { text: 'FR', id: 'FR' }
]

// Validation errors for standard inputs
const errors = reactive({
  firstName: '',
  lastName: '',
  emailAddress: '',
  birthDateError: '',
  password: ''
})

// Validation functions for standard inputs
const validateEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  return emailRegex.test(email)
}

const validateFirstName = (): boolean => {
  const nameRegex = /^[\p{L}\s'-]+$/u

  if (!formInitialValues.firstName?.trim()) {
    errors.firstName = t('consultantCorner.contactDetails.FirstNameRequired')
    return false
  } else if (!nameRegex.test(formInitialValues.firstName)) {
    errors.firstName = t('validators.nameField', { fieldName: t('columns.firstName') })
    return false
  } else if (formInitialValues.firstName.trim().length < 2) {
    errors.firstName = t('validators.fieldMinLength', { fieldName: t('columns.firstName'), minimumLength: 2 })
    return false
  } else if (formInitialValues.firstName.length > 50) {
    errors.firstName = t('validators.fieldLength', { fieldName: t('columns.firstName'), maximumLength: 50 })
    return false
  }

  errors.firstName = ''
  return true
}

const validateLastName = (): boolean => {
  const nameRegex = /^[\p{L}\s'-]+$/u

  if (!formInitialValues.lastName?.trim()) {
    errors.lastName = t('consultantCorner.contactDetails.LastNameRequired')
    return false
  } else if (!nameRegex.test(formInitialValues.lastName)) {
    errors.lastName = t('validators.nameField', { fieldName: t('columns.lastName') })
    return false
  } else if (formInitialValues.lastName.trim().length < 2) {
    errors.lastName = t('validators.fieldMinLength', { fieldName: t('columns.lastName'), minimumLength: 2 })
    return false
  } else if (formInitialValues.lastName.length > 50) {
    errors.lastName = t('validators.fieldLength', { fieldName: t('columns.lastName'), maximumLength: 50 })
    return false
  }

  errors.lastName = ''
  return true
}

const validateEmailAddress = (): boolean => {
  if (!formInitialValues.emailAddress?.trim()) {
    errors.emailAddress = t('consultantCorner.contactDetails.EmailRequired')
    return false
  } else if (!validateEmail(formInitialValues.emailAddress)) {
    errors.emailAddress = t('consultantCorner.contactDetails.EmailInvalid')
    return false
  }

  errors.emailAddress = ''
  return true
}

const validateBirthDate = (): boolean => {
  if (txtBirthDate.value == null) {
    errors.birthDateError = t('validators.birthDateRequired')
    return false
  }

  if (!validateAge(txtBirthDate.value)) {
    errors.birthDateError = t('validators.minimumAge')
    return false
  }

  errors.birthDateError = ''
  return true
}

const txtBirthDate = ref<Date | undefined>(undefined)
const returnURL = ref<string | null>('')
const passwordRegex = ref<string>('')
const showPasswordPattern = ref<boolean>(false)
const isSubmitting = ref<boolean>(false)
const isDefaultWebAccountMode = ref<boolean>(false)
const beeNumberToken = ref<string | null>(null)

const validatePassword = (): boolean => {
  const passwordReg = new RegExp(passwordRegex.value)

  if (!formInitialValues.password?.trim()) {
    errors.password = t('validators.requiredField', { fieldName: t('columns.password') })
    return false
  }

  const prohibitedCharMessage = getPasswordProhibitedCharMessage(t, passwordRegex.value, formInitialValues.password)
  if (prohibitedCharMessage) {
    errors.password = prohibitedCharMessage
    return false
  }

  if (!passwordReg.test(formInitialValues.password)) {
    errors.password = t('consultantCorner.security.newPasswordWeak')
    return false
  }

  errors.password = ''
  return true
}

// Computed min/max dates for birthday field to constrain valid range
const minBirthDate = computed(() => {
  const date = new Date()
  date.setFullYear(date.getFullYear() - 120) // Max 120 years old
  return date
})

const maxBirthDate = computed(() => {
  const date = new Date()
  date.setFullYear(date.getFullYear() - 18) // Min 18 years old
  return date
})

// Add error handling state
const errorMessage = ref('')
const hasError = ref(false)


// Add marketing opt-in state
const campaignTypes = ref<IShareCddCodeGetListResponseItem[]>([])
const selectedCampaigns = ref<Set<string>>(new Set())
const marketingOptInLoading = ref(false)
const marketingOptInError = ref('')

onMounted(async () => {
  logger.debug('onMounted')

  passwordRegex.value = await GetPasswordRegex();

  const urlParams = new URLSearchParams(window.location.search)
  returnURL.value = urlParams.get('returnUrl')
  beeNumberToken.value = getQueryStringValue(urlParams, 'beenumbertoken')

  logger.debug('AoeRegistration', 'onMounted' ,'returnURL', returnURL.value)

  if (beeNumberToken.value) {
    await loadDefaultWebAccount(beeNumberToken.value)
  }

  const countryCode = ref('')
  countryCode.value = await GetCountryCode()

  GetCountriesFromLocalStorageOrApi().then((data) => {
    if (data?.items) {
      for (const country of data?.items) {
        const translatedName = t(getCountryLabelKey(country.countryCode))
        countries.value.push({ text: translatedName, id: country.countryCode })

        if (country.countryCode === countryCode.value) {
          selectedCountry.value = { text: translatedName, id: country.countryCode }
        }
      }

      countries.value.sort((a, b) => {
        return a.text.localeCompare(b.text)
      })
    }
  })

  // Load marketing campaign types
  loadCampaignTypes()

})

function getQueryStringValue(urlParams: URLSearchParams, key: string): string | null {
  const queryParam = Array.from(urlParams.entries()).find(([paramKey]) => paramKey.toLowerCase() === key.toLowerCase())
  return queryParam?.[1] ?? null
}

async function loadDefaultWebAccount(beeNumberToken: string) {
  try {
    const response = await BeeEntityWebAccountDefaultGet(beeNumberToken) as IDefaultWebAccountResponse | null
    const defaultAccount = response?.item

    Object.assign(formInitialValues, {
      firstName: defaultAccount?.firstName ?? '',
      lastName: defaultAccount?.lastName ?? '',
      emailAddress: defaultAccount?.emailAddress ?? '',
    })
    isDefaultWebAccountMode.value = true
  } catch (error) {
    logger.error('Error loading default web account:', error)
    await handleError(error, t)
  }
}

async function loadCampaignTypes() {
  try {
    marketingOptInLoading.value = true
    marketingOptInError.value = ''

    const availableCampaignTypes = await ShareCddCodeGetList({ CategoryId: 'CAMPAIGNTYPE' })

    if (availableCampaignTypes?.codes) {
      campaignTypes.value = availableCampaignTypes.codes
      logger.debug('Loaded campaign types:', campaignTypes.value.length)
    }
  } catch (error) {
    logger.error('Error loading campaign types:', error)
    marketingOptInError.value = t('errors.loadingCampaignTypes')
  } finally {
    marketingOptInLoading.value = false
  }
}

function toggleCampaignSelection(campaignId: string) {
  if (selectedCampaigns.value.has(campaignId)) {
    selectedCampaigns.value.delete(campaignId)
  } else {
    selectedCampaigns.value.add(campaignId)
  }
  logger.debug('Selected campaigns:', Array.from(selectedCampaigns.value))
}

function getCampaignLabel(codeId: string): string {
  const labelKey = `labels.MarketingOptIn_${codeId}_Content`
  // Try to get the specific label, fall back to the campaign's localizedValue or name
  const specificLabel = t(labelKey)

  // If the translation key doesn't exist, t() returns the key itself
  if (specificLabel === labelKey) {
    // Fall back to finding the campaign in the list and using its localizedValue or name
    const campaign = campaignTypes.value.find(c => c.codeId === codeId)
    return campaign?.localizedValue || campaign?.name || codeId
  }

  return specificLabel
}

function countryChangeHandler(country: Country | undefined) {
  logger.debug('countryChangeHandler', country)
  if (country) {
    selectedCountry.value = country
  }
}

function onSubmitClickHandler(event: Event) {
  event.preventDefault()
  logger.debug('onSubmitClickHandler')
  void performSubmit()
}

async function performSubmit() {
  // Prevent double submission
  if (isSubmitting.value) {
    return
  }
  isSubmitting.value = true

  logger.debug('performSubmit')

  // Validate birthday (required + age)
  if (!validateBirthDate()) {
    logger.debug('Form is not valid')
    isSubmitting.value = false
    return
  }

  // Validate phone controls
  if (primaryPhoneControl.value && !primaryPhoneControl.value.validate()) {
    isSubmitting.value = false
    return
  }
  if (mobilePhoneControl.value && !mobilePhoneControl.value.validate()) {
    isSubmitting.value = false
    return
  }

  // Validate standard inputs
  if (!validateFirstName() || !validateLastName() || !validateEmailAddress() || !validatePassword()) {
    isSubmitting.value = false
    return
  }

  logger.debug('Form is valid')

  // Reset any previous errors
  hasError.value = false
  errorMessage.value = ''

  SetIsLoading(true)
  const formValues = { ...formInitialValues } as IAoeRegistration

  if (isDefaultWebAccountMode.value) {
    formValues.emailAddress = formInitialValues.emailAddress
  }

  // TEAU-1707: the token is intentionally not sent to the backend. Its presence there marks the
  // email as verified on create and suppresses the WebAccountVerification email

  // Format birthDate as date-only string (YYYY-MM-DD) without timestamp
  if (txtBirthDate.value) {
    const year = txtBirthDate.value.getFullYear()
    const month = String(txtBirthDate.value.getMonth() + 1).padStart(2, '0')
    const day = String(txtBirthDate.value.getDate()).padStart(2, '0')
    formValues.birthDate = `${year}-${month}-${day}` as any
  }

  formValues.countryCode = selectedCountry.value.id
  formValues.cultureName = getCultureNameFromCountryCode(selectedCountry.value.id)
  formValues.primaryPhone = {
    number: primaryPhoneModel.value.number,
    countryCode: primaryPhoneModel.value.countryCode,
    codeIdPhoneType: primaryPhoneModel.value.codeIdPhoneType
  }
  formValues.mobilePhone = {
    number: mobilePhoneModel.value.number,
    countryCode: mobilePhoneModel.value.countryCode,
    codeIdPhoneType: mobilePhoneModel.value.codeIdPhoneType
  }

  // Add marketing opt-in preferences
  formValues.marketingOptIns = Array.from(selectedCampaigns.value)

  const allCampaignIds = campaignTypes.value.map(c => c.codeId)
  formValues.marketingOptOuts = allCampaignIds.filter(id => !selectedCampaigns.value.has(id))

  // User-initiated registration — tell the backend to send the AccountCreationEmail template.
  formValues.webAccount = true

  CreateAccount(formValues).then(async (result) => {
    logger.debug('CreateAccount result:', result)

    // Check for error response
    if (result && result.responseStatus && result.responseStatus.errorCode) {
      // Set error flag
      hasError.value = true

      // Always use the message from responseStatus
      errorMessage.value = result.responseStatus.message

      // Scroll to the error message
      setTimeout(() => {
        const errorElement = document.querySelector('.registration-error')
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
    } else if (result) {
      trackRegister()
      GetLoginUrl().then((url) => {
        if (url) {
          const safeReturnUrl = UrlHelpers.getSafeReturnUrl(returnURL.value)
          if (safeReturnUrl) {
            window.location.href = `${url}?returnURL=${encodeURIComponent(safeReturnUrl)}`
          } else {
            window.location.href = url
          }
        }
      })
    }

    SetIsLoading(false)
    isSubmitting.value = false
  }).catch(error => {
    logger.error('CreateAccount error:', error)
    hasError.value = true

    // Use error.responseStatus.message if available, otherwise use a generic message
    if (error && error.response && error.response.statusText) {
      errorMessage.value = error.response.statusText
    } else {
      errorMessage.value = 'An unexpected error occurred during registration'
    }

    SetIsLoading(false)
    isSubmitting.value = false
  })
}

function validateAge(birthDate: Date): boolean {
  const today = new Date()
  const age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()

  // Check if birthday has occurred this year
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    return (age - 1) >= 18
  }

  return age >= 18
}

function birthDateChangeHandler(e: unknown) {
  const v = (e as { value?: unknown })?.value
  const date = (v instanceof Date && !Number.isNaN(v.getTime()) && v.getFullYear() >= 1000) ? v : undefined
  txtBirthDate.value = date
  logger.debug('AoeRegistration', 'birthDateChangeHandler', 'txtBirthDate.value:', txtBirthDate.value)

  // Validate birthday (required + age)
  validateBirthDate()
}

function formatDateForInput(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function passwordChangeHandler() {
  logger.debug('passwordChangeHandler')

  const passwordReg = new RegExp(passwordRegex.value);

  // Test with the form value
  if (formInitialValues.password && !passwordReg.test(formInitialValues.password)) {
    showPasswordPattern.value = true;
  } else {
    showPasswordPattern.value = false;
  }
}
</script>

<template>
  <div class="aoe-registration">
    <form @submit="onSubmitClickHandler">
        <div class="row regRow">
          <div class="col-12">
            <p class="pageHeader">{{t('labels.RegistrationHeader')}}</p>
            <!-- Error message display -->
            <div v-if="hasError" class="alert alert-danger registration-error" role="alert">
              {{ errorMessage }}
            </div>
          </div>
        </div>
        <div class="regInfo">
          <fieldset class="k-form-fieldset">
            <div class="row mt-3 regRow">
              <div class="col-12">
                <div class="form-field">
                  <label class="form-label required">{{ t('labels.Country') }}</label>
                  <select
                    v-model="selectedCountry.id"
                    class="form-select"
                    @change="countryChangeHandler(selectedCountry)"
                  >
                    <option v-for="country in countries" :key="country.id" :value="country.id">
                      {{ country.text }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            <div class="row regRow">
              <div class="col-12">
                <div class="form-field">
                  <label class="form-label required">{{ t('columns.firstName') }}</label>
                  <input
                    v-model="formInitialValues.firstName"
                    type="text"
                    class="form-input"
                    :class="{ 'error': errors.firstName }"
                    required
                    @input="validateFirstName"
                    @blur="validateFirstName"
                  />
                  <div v-if="errors.firstName" class="error-message">{{ errors.firstName }}</div>
                </div>
              </div>
            </div>
            <div class="row regRow">
              <div class="col-12">
                <div class="form-field">
                  <label class="form-label required">{{ t('columns.lastName') }}</label>
                  <input
                    v-model="formInitialValues.lastName"
                    type="text"
                    class="form-input"
                    :class="{ 'error': errors.lastName }"
                    required
                    @input="validateLastName"
                    @blur="validateLastName"
                  />
                  <div v-if="errors.lastName" class="error-message">{{ errors.lastName }}</div>
                </div>
              </div>
            </div>
            <div class="row regRow">
              <div class="col-12">
                <div class="form-field">
                  <label class="form-label required">{{ t('columns.emailAddress') }}</label>
                  <input
                    v-model="formInitialValues.emailAddress"
                    type="email"
                    class="form-input"
                    :class="{ 'error': errors.emailAddress }"
                    required
                    :disabled="isDefaultWebAccountMode"
                    @input="validateEmailAddress"
                    @blur="validateEmailAddress"
                  />
                  <div v-if="errors.emailAddress" class="error-message">{{ errors.emailAddress }}</div>
                </div>
              </div>
            </div>
            <div class="row regRow mt-3">
              <div class="col-12">
                <div class="form-field">
                  <label class="form-label required">{{ t('labels.BirthDate') }}</label>
                  <LocalizationProvider :language="getKendoLanguage(locale)">
                    <IntlProvider :locale="getKendoLocale(locale)">
                      <KDatePicker
                        :value="txtBirthDate"
                        :min="minBirthDate"
                        :max="maxBirthDate"
                        :placeholder="t('columns.birthDatePlaceholder')"
                        :format="'dd/MM/yyyy'"
                        :format-placeholder="'day/month/year'"
                        :start="'decade'"
                        :depth="'year'"
                        :class="{ 'k-invalid': errors.birthDateError }"
                        @change="birthDateChangeHandler($event)"
                      />
                    </IntlProvider>
                  </LocalizationProvider>
                  <div v-if="errors.birthDateError" class="error-message">{{ errors.birthDateError }}</div>
                </div>
              </div>
            </div>
            <div class="row regRow">
              <div class="col-12">
                <div class="form-field">
                  <label class="form-label required">{{ t('columns.password') }}</label>
                  <input
                    v-model="formInitialValues.password"
                    type="password"
                    class="form-input"
                    :class="{ 'error': errors.password }"
                    required
                    @input="() => { validatePassword(); passwordChangeHandler() }"
                    @blur="validatePassword"
                  />
                  <div v-if="errors.password" class="error-message">{{ errors.password }}</div>
                </div>
              </div>
            </div>
            <div v-if="showPasswordPattern" class="row regRow password-pattern-div bg-light">
              <div
                class="col-12"
                v-html="t('validators.passwordPattern')">
              </div>
            </div>
            <div class="row regRow">
              <div class="col-12">
                <div class="form-field">
                  <label class="form-label">{{ t('consultantCorner.contactDetails.PrimaryPhone') }}</label>
                  <PhoneInputControl
                    ref="primaryPhoneControl"
                    v-model="primaryPhoneModel"
                    :required="false"
                    :showExtension="false"
                    :showPhoneType="false"
                    :countryList="phoneCountryOptions"
                    :placeholder="t('consultantCorner.contactDetails.PhonePlaceholder')"
                  />
                </div>
              </div>
            </div>
            <div class="row regRow">
              <div class="col-12">
                <div class="form-field">
                  <label class="form-label">{{ t('consultantCorner.contactDetails.MobilePhone') }}</label>
                  <PhoneInputControl
                    ref="mobilePhoneControl"
                    v-model="mobilePhoneModel"
                    :required="false"
                    :showExtension="false"
                    :showPhoneType="false"
                    :countryList="phoneCountryOptions"
                    :placeholder="t('consultantCorner.contactDetails.PhonePlaceholder')"
                  />
                </div>
              </div>
            </div>

            <!-- Marketing Opt-In Section -->
            <div class="row regRow mt-4">
              <div class="col-12">
                <div class="marketing-optin-section">
                  <h4 class="marketing-optin-title">{{ t('labels.MarketingOptIn') }}</h4>
                  <!-- Loading state -->
                  <div v-if="marketingOptInLoading" class="marketing-loading">
                    {{ t('labels.Loading') }}...
                  </div>

                  <!-- Error state -->
                  <div v-else-if="marketingOptInError" class="marketing-error">
                    {{ marketingOptInError }}
                  </div>

                  <!-- Campaign types list -->
                  <div v-else-if="campaignTypes.length > 0" class="campaign-types-list">
                    <div
                      v-for="campaign in campaignTypes"
                      :key="campaign.codeId"
                      class="campaign-item"
                    >
                      <label class="campaign-checkbox">
                        <input
                          type="checkbox"
                          :value="campaign.codeId"
                          @change="toggleCampaignSelection(campaign.codeId)"
                        />
                        <span class="campaign-checkmark"></span>
                        <div
                          class="campaign-name"
                          v-html="getCampaignLabel(campaign.codeId)">
                        </div>
                      </label>
                    </div>
                    <div><a href="/privacy-policy" target="_blank">{{t('terms.showMore')}}</a></div>
                  </div>
                </div>
              </div>
            </div>

            <div class="row regRow mt-3">
              <div class="col-12 buttonCreateAccountDiv">
                <button
                  ref="btnCreateAccount"
                  id="btnCreateAccount"
                  type="submit"
                  class="btn primaryBtn btnCreateAccount"
                  :disabled="isSubmitting"
                  >{{t('labels.CreateAccount')}}
                </button>
              </div>
            </div>
          </fieldset>
        </div>
    </form>
  </div>
</template>

<style lang="scss">

.password-pattern-div {
  border-radius: $radius-md;
  padding: $spacing-2-5;
  margin: $spacing-2-5;
}

.regRow {
  width: 50%;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: $spacing-3;
}

.buttonCreateAccountDiv
{
  text-align: center;
}

.age-validation-error {
  @include typography-small;
  color: #dc3545;
  margin-top: 0.25rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.form-label {
  @include typography-body;
  @include font-weight-medium;
  color: #333;
  margin-bottom: 0.5rem;
}

.form-label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-input {
  padding: 0.75rem;
  @include typography-body;
  border: 1px solid #ced4da;
  border-radius: $radius-sm;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-input:focus {
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(#007bff, 0.3);
}

.form-input.error {
  border-color: #dc3545;
}

.form-input.error:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(#dc2626, 0.25);
}

.form-select {
  width: 100%;
  padding: $spacing-2-5 $spacing-3;
  @include typography-body;
  border: 1px solid #ced4da;
  border-radius: $radius-sm;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  background-color: var(--color-background);
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right $spacing-2 center;
  background-size: 1em;
  cursor: pointer;
}

.form-select:focus {
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(#007bff, 0.3);
}

.form-select.error {
  border-color: #dc3545;
}

.form-select.error:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(#dc2626, 0.25);
}

.error-message {
  @include typography-small;
  color: #dc3545;
  margin-top: 0.25rem;
}

// Kendo DatePicker styling to match custom form inputs
.k-datepicker {
  width: 100%;
}

.k-datepicker .k-input {
  padding: 0.75rem;
  @include typography-body;
  border: 1px solid #ced4da;
  border-radius: $radius-sm;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.k-datepicker .k-input:focus {
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(#007bff, 0.3);
  border-color: #007bff;
}

.k-datepicker .k-input.k-invalid {
  border-color: #dc3545;
}

.k-datepicker .k-input.k-invalid:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(#dc2626, 0.25);
}

.marketing-optin-section {
  background: var(--vt-c-white-soft);
  border: 1px solid #e9ecef;
  border-radius: $radius-md;
  padding: $card-padding;
  margin-top: $spacing-4;
}

.marketing-optin-title {
  @include typography-body;
  @include font-weight-semibold;
  color: #333;
  margin: 0 0 8px 0;
}

.marketing-optin-description {
  @include typography-small;
  color: #666;
  margin: 0 0 16px 0;
  line-height: 1.4;
}

.marketing-loading {
  text-align: center;
  color: #666;
  font-style: italic;
  padding: $spacing-4;
}

.marketing-error {
  @include typography-small;
  color: #dc3545;
  padding: $spacing-3;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: $radius-sm;
}

.campaign-types-list {
  display: flex;
  flex-direction: column;
  gap: $spacing-3;
}

.campaign-item {
  display: flex;
  align-items: center;
}

.campaign-checkbox {
  display: flex;
  align-items: flex-start;
  @include typography-small;
  cursor: pointer;
  line-height: 1.4;
  position: relative;
  padding-left: 28px;
  margin: 0;
  gap: $gap-sm;
}

.campaign-checkbox input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.campaign-checkmark {
  position: absolute;
  top: 2px;
  left: 0;
  height: 18px;
  width: 18px;
  background-color: var(--vt-c-white);
  border: 2px solid #e0e0e0;
  border-radius: $radius-sm;
  transition: all 0.2s ease;
}

.campaign-checkbox:hover input ~ .campaign-checkmark {
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.campaign-checkbox input:checked ~ .campaign-checkmark {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.campaign-checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.campaign-checkbox input:checked ~ .campaign-checkmark:after {
  display: block;
}

.campaign-checkbox .campaign-checkmark:after {
  left: 5px;
  top: 2px;
  width: 4px;
  height: 8px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.campaign-name {
  @include font-weight-medium;
  color: #333;
  flex: 1;
  margin-top: 0;
}

.campaign-name p {
  margin: 0 0 8px 0;
  line-height: 1.4;
}

.campaign-name p:last-child {
  margin-bottom: 0;
}

.campaign-types-list a {
  @include typography-small;
  color: #007bff !important;
  text-decoration: underline !important;
  margin-top: 12px;
  display: inline-block;
}

.campaign-types-list a:hover {
  color: #0056b3 !important;
  text-decoration: underline !important;
}

</style>
